import React, { useEffect, useRef } from "react";
import { Segmented, Tabs } from "@/app/components/kit";
import { TabScope } from "@/app/components/shared/tab-panel";

export interface PrimaryTab {
  value: string;
  label: string;
}

export interface SecondaryFilter {
  value: string;
  label: string;
}

interface ModuleTabNavigationProps {
  primaryTabs: PrimaryTab[];
  activePrimaryTab: string;
  onPrimaryTabChange: (value: string) => void;
  secondaryFilters?: SecondaryFilter[];
  activeSecondaryFilter?: string;
  onSecondaryFilterChange?: (value: string) => void;
  secondaryRightContent?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * ModuleTabNavigation
 *
 * Two-tier navigation pattern with responsive behavior:
 * - Primary tabs row scrolls horizontally when labels overflow. A subtle
 *   right-edge fade indicates "more content".
 * - On mount AND on active-tab change, scrolls the active tab into view —
 *   so the default-active tab isn't pinned left while later tabs clip off
 *   the right.
 * - Font size, gaps, and padding shrink on mobile to minimize how often
 *   overflow happens in the first place.
 * - Secondary tier stacks filter pills above right-content (e.g. search)
 *   below `md:` so the search input gets its own full-width row.
 */
export function ModuleTabNavigation({
  primaryTabs,
  activePrimaryTab,
  onPrimaryTabChange,
  secondaryFilters,
  activeSecondaryFilter,
  onSecondaryFilterChange,
  secondaryRightContent,
  children,
}: ModuleTabNavigationProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll the active tab into view on mount AND whenever it changes.
  //
  // Why mount matters: on mobile, the first tab is usually active by
  // default, so it sits at the left edge of the scroll container and the
  // last tab ("Action Log") clips off the right. The user never saw a
  // change event, so a change-only effect never fires. Running this on
  // mount makes the page take stock of its own width and reveal whichever
  // tab is currently active, preserving affordance.
  //
  // A tiny RAF delay lets layout settle after mount so the container has
  // its final scrollWidth before we measure.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const raf = requestAnimationFrame(() => {
      const activeEl = container.querySelector<HTMLElement>(
        `[data-state="active"]`
      );
      if (!activeEl) return;

      // `nearest` so already-visible tabs don't jump around unnecessarily,
      // but clipped ones get revealed. We check ourselves first — if the
      // element is fully in view, skip the call entirely to avoid smooth-
      // scroll flicker on already-visible tabs.
      const cRect = container.getBoundingClientRect();
      const aRect = activeEl.getBoundingClientRect();
      const fullyVisible =
        aRect.left >= cRect.left && aRect.right <= cRect.right;
      if (fullyVisible) return;

      activeEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [activePrimaryTab]);

  return (
    <TabScope value={activePrimaryTab}>
      <div className="upm-tabs">
      <div className="upm-tabs__primary">

        <div ref={scrollRef} className="upm-tabs__scroll">
          <Tabs
            label="Module sections"
            items={primaryTabs}
            value={activePrimaryTab}
            onChange={onPrimaryTabChange}
          />
        </div>
      </div>

      {/* Secondary Filter Tier */}
      {secondaryFilters && secondaryFilters.length > 0 && (
        <div className="upm-tabs__secondary">
          <div className="upm-tabs__scroll">
            {/* Item 47's pill variant: one tab stop, arrows between options.
                It was nine plain buttons with no group role and no keyboard
                relationship. */}
            <Segmented
              variant="tabs"
              label="Filter"
              options={secondaryFilters}
              value={activeSecondaryFilter ?? secondaryFilters[0].value}
              onChange={(v) => onSecondaryFilterChange?.(v)}
            />
          </div>

          {secondaryRightContent && (
            <div className="upm-tabs__right">{secondaryRightContent}</div>
          )}
        </div>
      )}

      {children}
      </div>
    </TabScope>
  );
}